import React from 'react';
import './About.css';
import {
  FaGlobe,
  FaCode,
  FaMapMarkedAlt,
  FaBalanceScale,
  FaExternalLinkAlt
} from 'react-icons/fa';


const About: React.FC = () => {
  return (
    <div className="about">
      {/* Hero Header */}
      <section className="about-hero">
        <h1>About PACA</h1>
        <p className="hero-description">
          PACA (Paleocoordinates Calculator) is a scientific tool that enables researchers 
          to reconstruct ancient geographic coordinates using state-of-the-art Global Plate Models.
        </p>
      </section>


      {/* Main Grid */}
      <div className="about-grid">
        
        {/* Card 1: Models */}
        <article className="about-card models-card">
          <div className="card-header">
            <div className="card-icon">
              <FaGlobe />
            </div>
            <h2>Global Plate Models</h2>
          </div>
          
          <div className="card-body">
            <p className="card-intro">
              PACA integrates five leading Global Plate Models (GPMs) through the <strong>palaeoverse</strong> R package, 
              each representing a different scientific interpretation of Earth's tectonic evolution.
            </p>


            <div className="models-list">
              <div className="model-card">
                <div className="model-header">
                  <h3>MERDITH2021</h3>
                  <span className="model-range">0–1000 Ma</span>
                </div>
                <p className="model-author">Merdith et al., 2021</p>
              </div>


              <div className="model-card">
                <div className="model-header">
                  <h3>TorsvikCocks2017</h3>
                  <span className="model-range">0–540 Ma</span>
                </div>
                <p className="model-author">Torsvik & Cocks, 2016</p>
              </div>


              <div className="model-card">
                <div className="model-header">
                  <h3>MATTHEWS2016</h3>
                  <span className="model-range">0–410 Ma</span>
                </div>
                <p className="model-author">Matthews et al., 2016</p>
              </div>


              <div className="model-card">
                <div className="model-header">
                  <h3>GOLONKA</h3>
                  <span className="model-range">0–540 Ma</span>
                </div>
                <p className="model-author">Wright et al., 2013</p>
              </div>


              <div className="model-card">
                <div className="model-header">
                  <h3>PALEOMAP</h3>
                  <span className="model-range">0–1100 Ma</span>
                </div>
                <p className="model-author">Scotese, 2016</p>
              </div>
            </div>


            <div className="card-footer">
              <a href="https://palaeoverse.org/" target="_blank" rel="noopener noreferrer" className="external-link">
                Learn more at palaeoverse.org <FaExternalLinkAlt />
              </a>
            </div>
          </div>
        </article>


        {/* Card 2: Code */}
        <article className="about-card code-card">
          <div className="card-header">
            <div className="card-icon">
              <FaCode />
            </div>
            <h2>Open-Source Technology</h2>
          </div>
          
          <div className="card-body">
            <p className="card-intro">
              PACA leverages the power of <strong>R</strong> and the community-driven 
              <strong> palaeoverse</strong> package to provide accurate paleogeographic reconstructions.
            </p>

            <div className="tech-description">
              <p>
                At the core of PACA is the <strong>palaeorotate</strong> function, which performs 
                coordinate transformations across geological time. This open-source tool is maintained 
                by a global community of palaeobiological researchers, ensuring transparent methods 
                and reproducible research for scientific validity.
              </p>
            </div>

            <div className="tech-badges">
              <span className="badge">R Language</span>
              <span className="badge">Open Source</span>
              <span className="badge">Community-Driven</span>
              <span className="badge">Reproducible</span>
            </div>

            <div className="reference-card">
              <p className="reference-text">
                Jones, L. A. et al. (2023). Palaeoverse: A community-driven R package to support 
                palaeobiological analysis. <em>Methods in Ecology and Evolution, 14(9), 2189–2510.</em>
              </p>
              <a href="https://doi.org/10.1111/2041-210X.14099" target="_blank" rel="noopener noreferrer" className="doi-button">
                View Publication <FaExternalLinkAlt />
              </a>
            </div>
          </div>
        </article>


        {/* Card 3: Maps */}
        <article className="about-card maps-card">
          <div className="card-header">
            <div className="card-icon">
              <FaMapMarkedAlt />
            </div>
            <h2>Paleomap Visualizations</h2>
          </div>
          
          <div className="card-body">
            <p className="card-intro">
              All map visualizations are derived from Christopher Scotese's renowned 
              <strong> PALEOMAP Project</strong>, providing scientifically accurate reconstructions 
              of Earth's ancient geography.
            </p>


            <div className="citation-notice">
              <div className="notice-icon">⚠️</div>
              <div className="notice-content">
                <h4>Citation Required</h4>
                <p>
                  If you use or publish maps generated by PACA, you must cite the PALEOMAP Project 
                  in your work.
                </p>
              </div>
            </div>


            <div className="reference-card">
              <p className="reference-text">
                Scotese, C. R. (2016). <em>PALEOMAP PaleoAtlas for GPlates and the PaleoData Plotter Program.</em> 
                PALEOMAP Project.
              </p>
              <a href="http://www.earthbyte.org/paleomap--paleoatlas--for--gplates" target="_blank" rel="noopener noreferrer" className="doi-button">
                Visit EarthByte <FaExternalLinkAlt />
              </a>
            </div>
          </div>
        </article>


        {/* Card 4: Rights */}
        <article className="about-card rights-card">
          <div className="card-header">
            <div className="card-icon">
              <FaBalanceScale />
            </div>
            <h2>Usage & Citation</h2>
          </div>
          
          <div className="card-body">
            <p className="card-intro">
              All models and tools integrated in PACA are publicly available for academic research. 
              Proper attribution is essential for maintaining scientific integrity.
            </p>


            <div className="citation-requirements">
              <h4>When using PACA, please cite:</h4>
              <ul className="requirements-list">
                <li>
                  <span className="requirement-number">1</span>
                  <div>
                    <strong>PACA Tool</strong>
                    <p>Scholz-Murcia, N., et al. (2025)</p>
                  </div>
                </li>
                <li>
                  <span className="requirement-number">2</span>
                  <div>
                    <strong>Palaeoverse Package</strong>
                    <p>Jones, L. A., et al. (2023)</p>
                  </div>
                </li>
                <li>
                  <span className="requirement-number">3</span>
                  <div>
                    <strong>Global Plate Model(s)</strong>
                    <p>The specific GPM(s) you selected</p>
                  </div>
                </li>
                <li>
                  <span className="requirement-number">4</span>
                  <div>
                    <strong>PALEOMAP Project</strong>
                    <p>If you use map visualizations</p>
                  </div>
                </li>
              </ul>
            </div>


            <div className="cta-box">
              <p>For complete citation guidelines and BibTeX entries:</p>
              <a href="/paca/citation" className="cta-button">
                View Citation Page
              </a>
            </div>
          </div>
        </article>


      </div>
    </div>
  );
};


export default About;
